<template>
    <v-card flat tile class="sources-card">
        <div v-if="mode === 'url' && domainFilter" class="sources-filter-row px-4 py-2">
            <v-chip color="primary" class="sources-domain-chip" outlined clearable @click="clearDomainFilter"> Domain: {{ domainFilter }} <v-icon class="ml-1" small>mdi-close</v-icon> </v-chip>
        </div>
        <div v-if="minimal" class="top-sources-panel px-3 pb-3 pt-2">
            <div v-for="(item, index) in minimalPagedSources" :key="sourceKey(item)" class="ts-row" :class="{ clickable: mode === 'domain' }" @click="onMinimalRowClick(item)">
                <div class="ts-left">
                    <div class="fav_wrapper" :class="{ fail: faviconFail[item.domain] }">
                        <template v-if="!faviconFail[item.domain]">
                            <img class="fav" :src="faviconUrl(item.domain) + '&sz=64'" :alt="item.domain" @error="onFavError($event, item.domain)" />
                        </template>
                        <template v-else>
                            <div class="domain-placeholder">{{ item.domain ? item.domain.charAt(0).toUpperCase() : "?" }}</div>
                        </template>
                    </div>
                    <div class="ts-name-wrap">
                        <span class="ts-name">{{ sourceTitle(item) }}</span>
                        <span class="ts-meta">Rank #{{ minimalPageStartIndex + index + 1 }}</span>
                        <span v-if="mode === 'domain'" class="ts-hover-hint">
                            <v-icon x-small class="mr-1">mdi-cursor-default-click-outline</v-icon>
                            Show URLs of Domain
                        </span>
                    </div>
                </div>
                <div class="ts-right">
                    <span class="ts-value">{{ formatNum(item.responsesCount) }}</span>
                    <span
                        v-if="deltaMap[deltaKey(item)] && deltaMap[deltaKey(item)].responsesPct != null"
                        class="ts-delta"
                        :class="{ up: deltaMap[deltaKey(item)].responsesPct > 0, down: deltaMap[deltaKey(item)].responsesPct < 0 }">
                        <v-icon x-small class="mr-1">{{
                            deltaMap[deltaKey(item)].responsesPct > 0 ? "mdi-arrow-up" : deltaMap[deltaKey(item)].responsesPct < 0 ? "mdi-arrow-down" : "mdi-minus"
                        }}</v-icon>
                        {{ deltaMap[deltaKey(item)].responsesPct > 0 ? "+" : "" }}{{ deltaMap[deltaKey(item)].responsesPct.toFixed(1) }}%
                    </span>
                </div>
            </div>

            <div v-if="showMinimalFooter" class="ts-pagination-wrap d-flex align-center justify-space-between mt-1">
                <v-btn small text :disabled="options.page <= 1" @click="prevMinimalPage">
                    <v-icon left x-small>mdi-chevron-left</v-icon>
                    Prev
                </v-btn>
                <span class="ts-pagination-status">{{ options.page }} / {{ minimalTotalPages }}</span>
                <v-btn small text :disabled="options.page >= minimalTotalPages" @click="nextMinimalPage">
                    Next
                    <v-icon right x-small>mdi-chevron-right</v-icon>
                </v-btn>
            </div>

            <div v-if="!loading && !sources.length" class="ts-empty">No sources available</div>
        </div>

        <v-data-table
            v-else
            :class="['elevation-0 sources-table', { 'rows-clickable': mode === 'domain' || mode === 'url' }]"
            :headers="headers"
            :items="sources"
            :loading="loading"
            :server-items-length="total"
            :options="options"
            :items-per-page="options.itemsPerPage"
            :footer-props="footerProps"
            :hide-default-footer="false"
            fixed-header
            height="70vh"
            id="monitoringSourcesTable"
            @update:options="onOptionsUpdate"
            @click:row="onRowClick">
            <!-- Gemeinsame Header Slots -->
            <template v-for="h in headers" v-slot:[`header.${h.value}`]="{ header }">
                <span class="hdr-wrapper" :key="h.value">
                    <v-btn v-if="header.filterable && !minimal && mode === 'domain'" icon x-small class="mr-1 mb-1" color="primary" @click.stop="openFilterMenu(header.value)">
                        <v-icon small>mdi-filter-variant</v-icon>
                    </v-btn>
                    <span class="hdr-label" :style="minimal ? 'font-weight:600' : ''">
                        {{ header.text }}
                        <v-tooltip top max-width="260" v-if="header.tooltip">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon small color="grey" class="ml-1 hdr-info" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                            </template>
                            <span v-html="header.tooltip"></span>
                        </v-tooltip>
                        <v-tooltip top max-width="260" v-if="isDeltaSortableHeader(header)">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    v-bind="attrs"
                                    v-on="on"
                                    x-small
                                    icon
                                    class="sources-sort-mode-btn ml-1"
                                    :class="{ active: getSourceSortMode(header.value) === 'delta' }"
                                    @click.stop="toggleSourceSortMode(header.value)">
                                    <v-icon x-small>{{ getSourceSortIcon(header.value) }}</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ getSourceSortMode(header.value) === "delta" ? "Sort mode: change (%)" : "Sort mode: absolute value" }}</span>
                        </v-tooltip>
                    </span>
                </span>
            </template>

            <!-- Domain Ansicht -->
            <template v-if="mode === 'domain'" v-slot:[`item.domain`]="{ item }">
                <div class="domain-cell" :class="{ mini: minimal }">
                    <div class="fav_wrapper" :class="{ fail: faviconFail[item.domain] }">
                        <template v-if="!faviconFail[item.domain]">
                            <img class="fav" :src="faviconUrl(item.domain) + '&sz=64'" :alt="item.domain" @error="onFavError($event, item.domain)" />
                        </template>
                        <template v-else>
                            <div class="domain-placeholder">{{ item.domain ? item.domain.charAt(0).toUpperCase() : "?" }}</div>
                        </template>
                    </div>
                    <div class="domain-meta">
                        <div class="domain-text" :title="item.domain">{{ item.domain }}</div>
                        <div v-if="!minimal && item.sampleUrl" class="domain-secondary">
                            {{ shortUrl(item.sampleUrl) }}
                        </div>
                    </div>
                </div>
            </template>

            <!-- URL Ansicht -->
            <template v-else v-slot:[`item.url`]="{ item }">
                <div class="domain-cell url-mode" :class="{ mini: minimal }">
                    <div class="fav_wrapper" :class="{ fail: faviconFail[item.domain] }">
                        <template v-if="!faviconFail[item.domain]">
                            <img class="fav" :src="faviconUrl(item.domain) + '&sz=64'" :alt="item.domain" @error="onFavError($event, item.domain)" />
                        </template>
                        <template v-else>
                            <div class="domain-placeholder">{{ item.domain ? item.domain.charAt(0).toUpperCase() : "?" }}</div>
                        </template>
                    </div>
                    <div class="domain-meta">
                        <div class="url-text" :title="item.url">{{ item.url }}</div>
                        <div v-if="!minimal" class="domain-secondary">{{ item.domain }}</div>
                    </div>
                </div>
            </template>

            <template v-if="mode === 'url'" v-slot:[`item.page_type`]="{ item }">
                <span class="page-type-chip" :class="{ empty: !item || !item.page_type }" :style="getPageTypeChipStyle(item && item.page_type)">
                    <span class="page-type-chip__dot" :style="{ backgroundColor: getPageTypeColor(item && item.page_type) }"></span>
                    <span class="page-type-chip__label">{{ formatPageTypeLabel(item && item.page_type) }}</span>
                </span>
            </template>

            <!-- Responses -->
            <template v-slot:[`item.responsesCount`]="{ item }">
                <div class="metric-wrapper">
                    <span class="metric-value">{{ formatNum(item.responsesCount) }}</span>
                    <template v-if="deltaMap[deltaKey(item)] && deltaMap[deltaKey(item)].responsesPct != null">
                        <span class="delta-chip" :class="{ up: deltaMap[deltaKey(item)].responsesPct > 0, down: deltaMap[deltaKey(item)].responsesPct < 0 }">
                            <v-icon x-small left v-if="deltaMap[deltaKey(item)].responsesPct !== 0">
                                {{ deltaMap[deltaKey(item)].responsesPct > 0 ? "mdi-arrow-up" : "mdi-arrow-down" }}
                            </v-icon>
                            {{ deltaMap[deltaKey(item)].responsesPct > 0 ? "+" : "" }}{{ deltaMap[deltaKey(item)].responsesPct.toFixed(1) }}%
                        </span>
                    </template>
                </div>
            </template>

            <!-- Items -->
            <template v-slot:[`item.itemsCount`]="{ item }">
                <div class="metric-wrapper">
                    <span class="metric-value">{{ formatNum(item.itemsCount) }}</span>
                    <template v-if="deltaMap[deltaKey(item)] && deltaMap[deltaKey(item)].itemsPct != null">
                        <span class="delta-chip" :class="{ up: deltaMap[deltaKey(item)].itemsPct > 0, down: deltaMap[deltaKey(item)].itemsPct < 0 }">
                            <v-icon x-small left v-if="deltaMap[deltaKey(item)].itemsPct !== 0">
                                {{ deltaMap[deltaKey(item)].itemsPct > 0 ? "mdi-arrow-up" : "mdi-arrow-down" }}
                            </v-icon>
                            {{ deltaMap[deltaKey(item)].itemsPct > 0 ? "+" : "" }}{{ deltaMap[deltaKey(item)].itemsPct.toFixed(1) }}%
                        </span>
                    </template>
                </div>
            </template>

            <!-- Coverage nur Domain-Modus -->
            <template v-if="mode === 'domain'" v-slot:[`item.responseCoverage`]="{ item }">
                <div class="coverage-cell">
                    <div class="cov-bar ml-auto">
                        <div class="cov-bar-fill" :style="{ width: ((item.responseCoverage || 0) * 100).toFixed(1) + '%' }"></div>
                    </div>
                    <span class="mono cov-val">{{ ((item.responseCoverage || 0) * 100).toFixed(1) }}%</span>
                    <template v-if="deltaMap[deltaKey(item)] && deltaMap[deltaKey(item)].coveragePct != null">
                        <span class="delta-badge" :class="{ up: deltaMap[deltaKey(item)].coveragePct > 0, down: deltaMap[deltaKey(item)].coveragePct < 0 }">
                            <v-icon x-small v-if="deltaMap[deltaKey(item)].coveragePct > 0">mdi-arrow-up</v-icon>
                            <v-icon x-small v-else-if="deltaMap[deltaKey(item)].coveragePct < 0">mdi-arrow-down</v-icon>
                            {{ deltaMap[deltaKey(item)].coveragePct > 0 ? "+" : "" }}{{ deltaMap[deltaKey(item)].coveragePct.toFixed(1) }}%
                        </span>
                    </template>
                </div>
            </template>
        </v-data-table>

        <v-dialog v-model="urlPanelOpen" max-width="560" transition="slide-x-reverse-transition" content-class="right-side-dialog" overlay-opacity="0.35">
            <v-card class="url-panel-card">
                <v-toolbar flat class="url-panel-toolbar">
                    <v-toolbar-title class="url-panel-title-wrap">
                        <div class="url-panel-title">URL Prompt Responses</div>
                        <div class="url-panel-subtitle">Prompt responses where this exact URL was used as source.</div>
                    </v-toolbar-title>
                    <v-spacer />
                    <v-btn icon @click="urlPanelOpen = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>

                <v-card-text class="pt-3">
                    <div class="url-panel-meta mb-3" v-if="selectedUrlRow">
                        <div class="url-panel-url-row">
                            <div class="url-panel-favicon-wrap" :class="{ fail: faviconFail[selectedUrlRow.domain] }">
                                <template v-if="!faviconFail[selectedUrlRow.domain]">
                                    <img
                                        class="url-panel-favicon"
                                        :src="faviconUrl(selectedUrlRow.domain) + '&sz=64'"
                                        :alt="selectedUrlRow.domain || 'url favicon'"
                                        @error="onFavError($event, selectedUrlRow.domain)" />
                                </template>
                                <template v-else>
                                    <div class="url-panel-favicon-fallback">{{ selectedUrlRow.domain ? selectedUrlRow.domain.charAt(0).toUpperCase() : "?" }}</div>
                                </template>
                            </div>
                            <a class="url-panel-url" :href="normalizeUrlForOpen(selectedUrlRow.url)" target="_blank" rel="noopener" :title="selectedUrlRow.url" @click.stop>
                                {{ selectedUrlRow.url }}
                            </a>
                        </div>
                        <div class="url-panel-domain" v-if="selectedUrlRow.domain">{{ selectedUrlRow.domain }}</div>
                    </div>

                    <v-progress-linear v-if="urlPanelLoading" indeterminate height="2" class="mb-3" />

                    <div v-if="!urlPanelLoading && !urlPanelResponses.length" class="caption grey--text">No prompt responses found for this URL.</div>

                    <div v-else class="url-panel-list">
                        <div v-for="resp in urlPanelResponses" :key="resp._id || resp.task_id" class="url-panel-item" @click="openPromptDetail(resp)">
                            <div class="url-panel-item__head">
                                <span class="url-panel-item__platform-wrap">
                                    <span class="url-panel-platform-icon-wrapper" v-if="getPlatformInfo(resp.platform).logo">
                                        <span class="url-panel-platform-icon" v-html="getPlatformInfo(resp.platform).logo"></span>
                                    </span>
                                    <span class="url-panel-item__platform">{{ getPlatformInfo(resp.platform).label || resp.platform || "—" }}</span>
                                </span>
                                <span class="url-panel-item__date">{{ formatDate(resp.importDate) }}</span>
                            </div>
                            <div class="url-panel-item__entities" v-if="getResponseEntities(resp).length">
                                <v-chip
                                    v-for="(ent, idx) in getResponseEntities(resp).slice(0, 4)"
                                    :key="(resp._id || resp.task_id || idx) + '-ent-' + idx"
                                    x-small
                                    outlined
                                    class="mr-1 mb-1 url-entity-chip"
                                    :style="entityChipStyle(ent)">
                                    <span class="entity-chip-dot" :style="entityDotStyle(ent)"></span>
                                    {{ ent.label }}
                                </v-chip>
                                <v-tooltip bottom v-if="getResponseEntities(resp).length > 4" max-width="320">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-chip x-small outlined class="mr-1 mb-1 url-entity-chip-more" v-bind="attrs" v-on="on"> +{{ getResponseEntities(resp).length - 4 }} </v-chip>
                                    </template>
                                    <div class="url-entity-tooltip-list">
                                        <div v-for="(ent, eIdx) in getResponseEntities(resp).slice(4)" :key="(resp._id || resp.task_id || eIdx) + '-hidden-ent-' + eIdx" class="url-entity-tooltip-row">
                                            <span class="entity-chip-dot" :style="entityDotStyle(ent)"></span>
                                            <span>{{ ent.label }}</span>
                                        </div>
                                    </div>
                                </v-tooltip>
                            </div>
                            <div class="url-panel-item__prompt" v-if="resp.prompt">{{ resp.prompt }}</div>
                            <div class="url-panel-item__response" v-if="resp.responseText">{{ resp.responseText }}</div>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
    import FilterMenu from "../monitoring/FilterMenu.vue"
    import { getBrandColorFromName } from "../../utils/brandColor"
    export default {
        name: "SourcesTable",
        inject: {
            // Von AIProject bereitgestellt: öffnet das Prompt-Detail als Overlay statt per Route
            aiSuiteOpenPromptDetail: { default: null }
        },
        components: { FilterMenu },
        props: {
            project_id: String,
            tags: Array,
            tagsOperator: String,
            selectedDates: Array,
            search: String,
            modeValue: { type: String, default: null },
            pageTypeFilter: { type: String, default: null },
            isActive: { type: Boolean, default: false },
            selectedPlatform: Object,
            project: Object, // { _id, platforms, prompt_count }
            brands: { type: Array, default: () => [] },
            exportData: Number,
            googlePromptCount: Number,
            minimal: { type: Boolean, default: false },
            importDatesByPlatform: Array // [{platform:'chatgpt', importDates:[date1,date2]}, ...] vom Parent
        },
        data() {
            return {
                sources: [],
                oldestSources: [],
                deltaMap: {},
                loading: false,
                total: 0,
                options: {
                    page: 1,
                    itemsPerPage: 25,
                    sortBy: ["responsesCount"],
                    sortDesc: [true],
                    groupBy: [],
                    groupDesc: [],
                    mustSort: false,
                    multiSort: false
                },
                itemsPerPageDefault: 25,
                filterMenuOpen: false,
                filterMenuField: "",
                filters: [],
                faviconFail: {},
                _queuedOptions: null,
                _needsRefreshOnActivate: false,
                mode: "domain",
                domainFilter: null, // NEU: aktive Domain für URL-Drilldown
                urlPanelOpen: false,
                urlPanelLoading: false,
                urlPanelResponses: [],
                selectedUrlRow: null,
                sourceSortModes: {
                    responsesCount: "absolute",
                    itemsCount: "absolute",
                    responseCoverage: "absolute"
                }
            }
        },
        computed: {
            headers() {
                if (this.mode === "url") {
                    return [
                        { text: "URL", value: "url", align: "start", filterable: false, tooltip: "Reference source (full URL)." },
                        { text: "Page Type", value: "page_type", align: "start", filterable: false, tooltip: "Classified page type for the URL." },
                        { text: "Responses", value: "responsesCount", align: "end", tooltip: "Distinct responses that reference this URL." },
                        { text: "Items", value: "itemsCount", align: "end", tooltip: "Total mentions of this URL across responses." }
                    ]
                }
                // existing
                if (this.minimal) {
                    return [
                        { text: "Domain", value: "domain", align: "start", filterable: false, tooltip: "Reference domain." },
                        { text: "Responses", value: "responsesCount", align: "end", tooltip: "Distinct responses that reference this domain." }
                    ]
                }
                return [
                    { text: "Domain", value: "domain", align: "start", filterable: true, tooltip: "Reference domain." },
                    { text: "Coverage", value: "responseCoverage", align: "end", tooltip: "Share of responses that reference this domain." },
                    { text: "Responses", value: "responsesCount", align: "end", tooltip: "Distinct responses that reference this domain." },
                    { text: "Items", value: "itemsCount", align: "end", tooltip: "Total mentions (multiple per response possible)." }
                ]
            },

            footerProps() {
                if (this.minimal) return {}
                return {
                    itemsPerPageOptions: [10, 25, 50, 100],
                    showFirstLastPage: true
                }
            },
            showMinimalFooter() {
                return this.minimal
            },
            minimalTotalPages() {
                const ipp = this.options.itemsPerPage || this.itemsPerPageDefault
                if (!ipp) return 1
                return Math.max(1, Math.ceil(this.total / ipp))
            },
            minimalPagedSources() {
                if (!this.minimal) return this.sources
                const page = this.options.page || 1
                const ipp = this.options.itemsPerPage || this.itemsPerPageDefault || 5
                const start = (page - 1) * ipp
                return (this.sources || []).slice(start, start + ipp)
            },
            minimalPageStartIndex() {
                const page = this.options.page || 1
                const ipp = this.options.itemsPerPage || this.itemsPerPageDefault || 1
                return (page - 1) * ipp
            }
        },
        watch: {
            minimal: {
                immediate: true,
                handler(val) {
                    const ipp = val ? 5 : this.itemsPerPageDefault
                    this.options = { ...this.options, page: 1, itemsPerPage: ipp }
                    // fetchData folgt durch onOptionsUpdate Event sobald Table intern updated
                }
            },
            selectedDates() {
                if (this.isActive) this.resetAndFetch()
                else this._needsRefreshOnActivate = true
            },
            search() {
                if (!this.isActive) {
                    this._needsRefreshOnActivate = true
                    return
                }
                clearTimeout(this._searchDebounce)
                this._searchDebounce = setTimeout(() => this.resetAndFetch(), 400)
            },
            selectedPlatform() {
                if (this.isActive) this.resetAndFetch()
                else this._needsRefreshOnActivate = true
            },
            tags() {
                if (this.isActive) this.resetAndFetch()
                else this._needsRefreshOnActivate = true
            },
            pageTypeFilter() {
                if (this.isActive) this.resetAndFetch()
                else this._needsRefreshOnActivate = true
            },
            mode() {
                if (this.isActive) this.resetAndFetch()
                else this._needsRefreshOnActivate = true
            },
            modeValue: {
                immediate: true,
                handler(nextMode) {
                    if (nextMode === "domain" || nextMode === "url") {
                        this.setMode(nextMode)
                    }
                }
            },
            isActive(newVal) {
                if (!newVal) return
                if (!this.sources.length || this._needsRefreshOnActivate) {
                    this.resetAndFetch()
                }
            }
        },
        mounted() {
            if (this.isActive) this.fetchData()
        },
        methods: {
            setMode(nextMode) {
                if (!nextMode || this.mode === nextMode) return
                if (nextMode === "domain" && this.domainFilter) {
                    this.domainFilter = null
                }
                this.mode = nextMode
                this.$emit("update:mode", this.mode)
            },
            normalizePageTypeKey(value) {
                return (value || "unknown").toString().trim().toLowerCase()
            },
            filterRowsByPageType(rows) {
                if (this.mode !== "url") return Array.isArray(rows) ? rows : []
                const key = this.normalizePageTypeKey(this.pageTypeFilter)
                if (!this.pageTypeFilter || !key) return Array.isArray(rows) ? rows : []
                return (Array.isArray(rows) ? rows : []).filter((row) => this.normalizePageTypeKey(row && row.page_type) === key)
            },
            prevMinimalPage() {
                if (!this.minimal) return
                if (this.options.page > 1) {
                    this.options.page--
                }
            },
            nextMinimalPage() {
                if (!this.minimal) return
                if (this.options.page < this.minimalTotalPages) {
                    this.options.page++
                }
            },
            async exportTableData() {
                const { rows, deltaMap } = await this.fetchAllSourcesForExport()

                if (this.mode === "url") {
                    const cols = ["url", "domain", "itemsCount", "responsesCount", "Δresponses%", "Δitems%", "importDate"]
                    const header = cols.join(";")
                    const lines = rows.map((s) => {
                        const d = deltaMap[this.deltaKey(s)] || {}
                        const fullUrl = s.exactUrl || s.url || ""
                        return [fullUrl, s.domain || "", s.itemsCount, s.responsesCount, this.formatDeltaPct(d.responsesPct), this.formatDeltaPct(d.itemsPct), this.formatDate(s.importDate)]
                            .map((v) => `"${String(v).replace(/"/g, '""')}"`)
                            .join(";")
                    })
                    return [header, ...lines].join("\n")
                }

                const cols = ["domain", "itemsCount", "responsesCount", "itemsPerResponse", "responseCoverage", "Δresponses%", "Δitems%", "Δcoverage%", "ΔitemsPerResp%", "importDate"]
                const header = cols.join(";")
                const lines = rows.map((s) => {
                    const d = deltaMap[this.deltaKey(s)] || {}
                    return [
                        s.domain,
                        s.itemsCount,
                        s.responsesCount,
                        s.itemsPerResponse != null ? s.itemsPerResponse.toFixed(2) : "0.00",
                        s.responseCoverage != null ? (s.responseCoverage * 100).toFixed(1) + "%" : "0%",
                        this.formatDeltaPct(d.responsesPct),
                        this.formatDeltaPct(d.itemsPct),
                        this.formatDeltaPct(d.coveragePct),
                        this.formatDeltaPct(d.itemsPerRespPct),
                        this.formatDate(s.importDate)
                    ]
                        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
                        .join(";")
                })
                return [header, ...lines].join("\n")
            },
            buildDeltaMapForRows(newerRows, olderRows) {
                const keyField = this.mode === "url" ? "url" : "domain"
                const oldMap = {}
                ;(olderRows || []).forEach((s) => {
                    oldMap[s[keyField]] = s
                })

                const map = {}
                ;(newerRows || []).forEach((s) => {
                    const k = s[keyField]
                    const old = oldMap[k]
                    if (!old) {
                        map[k] = {
                            responsesPct: s.responsesCount ? 100 : 0,
                            itemsPct: s.itemsCount ? 100 : 0,
                            coveragePct: this.mode === "domain" && s.responseCoverage ? 100 : null,
                            itemsPerRespPct: null
                        }
                    } else {
                        map[k] = {
                            responsesPct: this.deltaPct(old.responsesCount, s.responsesCount),
                            itemsPct: this.deltaPct(old.itemsCount, s.itemsCount),
                            coveragePct: this.mode === "domain" ? this.deltaPct(old.responseCoverage, s.responseCoverage) : null,
                            itemsPerRespPct: this.deltaPct(old.itemsPerResponse, s.itemsPerResponse)
                        }
                    }
                })

                return map
            },
            async fetchAllSourcesForExport() {
                if (!this.selectedDates || !this.selectedDates.length) {
                    return {
                        rows: Array.isArray(this.sources) ? [...this.sources] : [],
                        deltaMap: { ...this.deltaMap }
                    }
                }

                const dates = [...this.selectedDates].sort()
                const oldest = dates[0]
                const newest = dates[dates.length - 1]

                let sort = {}
                if (this.options.sortBy && this.options.sortBy.length) {
                    sort[this.options.sortBy[0]] = this.options.sortDesc?.[0] ? -1 : 1
                } else {
                    sort = { responsesCount: -1 }
                }

                const endpointBase = this.mode === "url" ? "/ai-suite/sources/urls/get" : "/ai-suite/sources/get"
                const baseUrl =
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + endpointBase

                const baseCommon = {
                    company_id: localStorage.company_id,
                    project_id: this.project_id || this.$route.params.id,
                    search: this.search,
                    sort,
                    skip: 0,
                    limit: 0
                }

                if (this.filters && this.filters.length && this.mode === "domain") baseCommon.filters = this.filters
                if (this.tags && this.tags.length) {
                    baseCommon.tags = this.tags
                    baseCommon.tagsOperator = this.tagsOperator
                }
                if (this.mode === "url" && this.domainFilter) {
                    baseCommon.domainFilter = this.domainFilter
                }

                const isAll = this.selectedPlatform?.key === "all"
                const buildSinglePayload = (singleDate) => ({
                    ...baseCommon,
                    selectedDates: [singleDate],
                    platform: this.selectedPlatform?.key
                })
                const buildMultiPayload = () => {
                    if (!Array.isArray(this.importDatesByPlatform)) return null
                    return {
                        ...baseCommon,
                        platform: null,
                        importDatesByPlatform: this.importDatesByPlatform
                    }
                }

                let promptC = 0
                if (this.project.google) promptC += this.googlePromptCount
                let promptCount = this.project.platforms.length * this.project.keyword_count + promptC

                const computeMetrics = (list, divisor) => {
                    if (this.mode !== "domain") return
                    list.forEach((x) => {
                        x.responseCoverage = divisor ? x.responsesCount / divisor : x.responsesCount / promptCount
                    })
                }

                const fetchAllPagesSingleDate = async (singleDate) => {
                    const resp = await this.$helpers.axiosPost(baseUrl, {
                        ...buildSinglePayload(singleDate),
                        skip: 0,
                        limit: 0
                    })
                    return Array.isArray(resp?.data?.sources) ? resp.data.sources : []
                }

                const fetchAllPagesMultiDate = async (payload) => {
                    const resp = await this.$helpers.axiosPost(baseUrl, {
                        ...payload,
                        skip: 0,
                        limit: 0
                    })
                    const sourcesByDate = Array.isArray(resp?.data?.sourcesByDate) ? resp.data.sourcesByDate : []
                    const olderAll = Array.isArray(sourcesByDate[0]) ? sourcesByDate[0] : []
                    const newerAll = Array.isArray(sourcesByDate[1]) ? sourcesByDate[1] : []

                    return { olderAll, newerAll }
                }

                if (isAll) {
                    const multiPayload = buildMultiPayload()
                    if (!multiPayload) {
                        return {
                            rows: Array.isArray(this.sources) ? [...this.sources] : [],
                            deltaMap: { ...this.deltaMap }
                        }
                    }

                    const firstResp = await this.$helpers.axiosPost(baseUrl, { ...multiPayload, skip: 0, limit: 1 })
                    const divisorOld = firstResp.data?.totalResponsesByDate?.[0] ?? null
                    const divisorNew = firstResp.data?.totalResponsesByDate?.[1] ?? null
                    const { olderAll, newerAll } = await fetchAllPagesMultiDate(multiPayload)

                    computeMetrics(olderAll, divisorOld)
                    computeMetrics(newerAll, divisorNew)

                    const primary = newerAll && newerAll.length ? newerAll : olderAll
                    const filteredPrimary = this.filterRowsByPageType(primary)
                    const filteredOldest = this.filterRowsByPageType(olderAll)

                    return {
                        rows: filteredPrimary,
                        deltaMap: this.buildDeltaMapForRows(filteredPrimary, filteredOldest)
                    }
                }

                const [latestMetaResp, oldestMetaResp] = await Promise.all([
                    this.$helpers.axiosPost(baseUrl, { ...buildSinglePayload(newest), skip: 0, limit: 1 }),
                    this.$helpers.axiosPost(baseUrl, { ...buildSinglePayload(oldest), skip: 0, limit: 1 })
                ])
                const [newestList, oldestList] = await Promise.all([fetchAllPagesSingleDate(newest), fetchAllPagesSingleDate(oldest)])
                const divisorNew = latestMetaResp.data.totalResponsesByDate
                const divisorOld = oldestMetaResp.data.totalResponsesByDate

                computeMetrics(newestList, divisorNew)
                computeMetrics(oldestList, divisorOld)

                const filteredNewest = this.filterRowsByPageType(newestList)
                const filteredOldest = this.filterRowsByPageType(oldestList)

                return {
                    rows: filteredNewest,
                    deltaMap: this.buildDeltaMapForRows(filteredNewest, filteredOldest)
                }
            },
            formatDeltaPct(val) {
                if (val == null) return ""
                return (val > 0 ? "+" : "") + val.toFixed(1) + "%"
            },
            resetAndFetch() {
                this.options.page = 1
                this.fetchData()
            },
            openFilterMenu(fieldKey) {
                this.filterMenuField = null
                this.$nextTick(() => {
                    this.filterMenuField = fieldKey
                    this.filterMenuOpen = true
                })
            },
            filtersUpdated(filters) {
                this.filters = filters
                this.resetAndFetch()
            },
            faviconUrl(domain) {
                if (!domain) return ""
                return `https://s2.googleusercontent.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=32`
            },
            onFavError(e, domain) {
                if (e && e.target) e.target.style.display = "none"
                if (domain) this.$set(this.faviconFail, domain, true)
            },
            shortUrl(url) {
                if (!url) return ""
                try {
                    const u = new URL(url)
                    const path = u.pathname
                    return u.hostname.replace(/^www\./, "") + path
                } catch {
                    return url.slice(0, 60)
                }
            },
            formatNum(n) {
                if (n === null || n === undefined) return ""
                return Intl.NumberFormat().format(n)
            },
            formatDate(d) {
                if (!d) return ""
                return new Date(d).toLocaleDateString()
            },
            sourceKey(item) {
                if (!item) return ""
                return this.mode === "url" ? item.url || item.domain || "" : item.domain || item.url || ""
            },
            sourceTitle(item) {
                if (!item) return "-"
                if (this.mode === "url") {
                    return item.url || "-"
                }
                return item.domain || "-"
            },
            onMinimalRowClick(item) {
                if (this.mode !== "domain") return
                this.onRowClick(item)
            },
            onOptionsUpdate(newOpts) {
                // Vuetify liefert neues Objekt bei Page/Sort Wechsel
                const prev = this.options
                const pageChanged = prev.page !== newOpts.page
                const ippChanged = prev.itemsPerPage !== newOpts.itemsPerPage
                const sortChanged = JSON.stringify(prev.sortBy) !== JSON.stringify(newOpts.sortBy) || JSON.stringify(prev.sortDesc) !== JSON.stringify(newOpts.sortDesc)

                this.options = { ...newOpts }

                if (!(pageChanged || ippChanged || sortChanged)) return
                if (this.loading) {
                    this._queuedOptions = newOpts
                    return
                }
                this.fetchData().then(() => {
                    if (this._queuedOptions) {
                        const q = this._queuedOptions
                        this._queuedOptions = null
                        this.onOptionsUpdate(q)
                    }
                })
            },
            isDeltaSortableHeader(header) {
                const key = header?.value
                if (!key) return false
                if (key === "responseCoverage") return this.mode === "domain"
                return key === "responsesCount" || key === "itemsCount"
            },
            getSourceSortMode(key) {
                return this.sourceSortModes[key] || "absolute"
            },
            toggleSourceSortMode(key) {
                const current = this.getSourceSortMode(key)
                const next = current === "delta" ? "absolute" : "delta"
                this.$set(this.sourceSortModes, key, next)
                this.sortDisplayed()
            },
            getSourceSortIcon(key) {
                return this.getSourceSortMode(key) === "delta" ? "mdi-trending-up" : "mdi-swap-vertical"
            },
            getSortMetricValue(item, key) {
                if (!item || !key) return null

                const isDelta = this.getSourceSortMode(key) === "delta"
                if (isDelta) {
                    const d = this.deltaMap[this.deltaKey(item)] || {}
                    if (key === "responsesCount") return typeof d.responsesPct === "number" ? d.responsesPct : null
                    if (key === "itemsCount") return typeof d.itemsPct === "number" ? d.itemsPct : null
                    if (key === "responseCoverage") return typeof d.coveragePct === "number" ? d.coveragePct : null
                }

                if (key === "responseCoverage") return typeof item.responseCoverage === "number" ? item.responseCoverage : null
                if (typeof item[key] === "number") return item[key]

                const v = item[key]
                if (typeof v === "string") return v.toLowerCase()
                return v ?? null
            },
            sortDisplayed() {
                if (!this.options.sortBy || !this.options.sortBy.length) return
                const key = this.options.sortBy[0]
                const desc = this.options.sortDesc?.[0]
                this.sources.sort((a, b) => {
                    const va = this.getSortMetricValue(a, key)
                    const vb = this.getSortMetricValue(b, key)

                    const aMissing = va == null || Number.isNaN(va)
                    const bMissing = vb == null || Number.isNaN(vb)
                    if (aMissing && bMissing) return 0
                    if (aMissing) return 1
                    if (bMissing) return -1

                    if (typeof va === "string" || typeof vb === "string") {
                        const cmp = String(va).localeCompare(String(vb), undefined, { sensitivity: "base", numeric: true })
                        return desc ? -cmp : cmp
                    }

                    const diff = Number(va) - Number(vb)
                    if (!Number.isFinite(diff) || diff === 0) return 0
                    return desc ? -diff : diff
                })
            },
            deltaPct(oldVal, newVal) {
                if (oldVal == null || newVal == null) return null
                if (oldVal === 0) return newVal === 0 ? 0 : 100
                return ((newVal - oldVal) / oldVal) * 100
            },
            deltaKey(item) {
                return this.mode === "url" ? item.url : item.domain
            },
            sourceLabel(item) {
                if (!item) return "—"
                return this.mode === "url" ? item.url || item.domain || "—" : item.domain || item.url || "—"
            },
            formatPageTypeLabel(value) {
                if (!value || typeof value !== "string") return "—"
                const key = value.trim().toLowerCase()
                const map = {
                    blog_guide: "Blog Guide",
                    product_service_page: "Product / Service Page",
                    category_hub: "Category Hub",
                    homepage: "Homepage",
                    comparison_page: "Comparison Page",
                    recommendation_list: "Recommendation List",
                    use_case_editorial: "Use Case Editorial",
                    directory_profile: "Directory Profile",
                    review_platform: "Review Platform",
                    docs_help_api: "Docs / Help / API",
                    company_trust_page: "Company Trust Page",
                    news_press_editorial: "News / Press Editorial",
                    community_ugc: "Community UGC",
                    legal_utility: "Legal Utility",
                    unknown_other: "Unknown / Other",
                    article: "Article",
                    news: "News",
                    review: "Review",
                    comparison: "Comparison",
                    faq: "FAQ",
                    docs: "Documentation",
                    forum: "Forum",
                    video: "Video",
                    unknown: "Unknown"
                }
                if (map[key]) return map[key]
                return key.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase())
            },
            getPageTypeColor(value) {
                const key = (value || "unknown").toString().trim().toLowerCase()
                const map = {
                    blog_guide: "#60A5FA",
                    product_service_page: "#FBBF24",
                    category_hub: "#4ADE80",
                    homepage: "#A78BFA",
                    comparison_page: "#F472B6",
                    recommendation_list: "#22D3EE",
                    use_case_editorial: "#34D399",
                    directory_profile: "#F59E0B",
                    review_platform: "#2DD4BF",
                    docs_help_api: "#94A3B8",
                    company_trust_page: "#10B981",
                    news_press_editorial: "#F87171",
                    community_ugc: "#FB923C",
                    legal_utility: "#A3A3A3",
                    unknown_other: "#CBD5E1",
                    article: "#38BDF8",
                    news: "#F87171",
                    review: "#2DD4BF",
                    comparison: "#F472B6",
                    faq: "#C084FC",
                    docs: "#94A3B8",
                    forum: "#FB923C",
                    video: "#FB7185",
                    unknown: "#CBD5E1"
                }
                return map[key] || map.unknown
            },
            getPageTypeChipStyle(value) {
                if (!value) return null
                const color = this.getPageTypeColor(value)
                return {
                    borderColor: color
                }
            },
            emitOverviewUpdated() {
                const rows = Array.isArray(this.sources) ? this.sources : []
                const withDelta = rows
                    .map((item) => {
                        const d = this.deltaMap[this.deltaKey(item)] || {}
                        return {
                            label: this.sourceLabel(item),
                            domain: item?.domain || null,
                            url: item?.url || item?.sampleUrl || null,
                            page_type: item?.page_type || null,
                            subtype: item?.subtype || null,
                            content_format: item?.content_format || null,
                            intent: item?.intent || null,
                            confidence: typeof item?.confidence === "number" ? item.confidence : null,
                            responsesCount: Number(item?.responsesCount || 0),
                            coverage: typeof item?.responseCoverage === "number" ? item.responseCoverage : null,
                            responsesPct: typeof d.responsesPct === "number" ? d.responsesPct : null
                        }
                    })
                    .filter((x) => x && x.label && x.label !== "—")

                const winners = withDelta
                    .filter((x) => x.responsesPct != null && x.responsesPct > 0)
                    .sort((a, b) => b.responsesPct - a.responsesPct)
                    .slice(0, 3)
                const losers = withDelta
                    .filter((x) => x.responsesPct != null && x.responsesPct < 0)
                    .sort((a, b) => a.responsesPct - b.responsesPct)
                    .slice(0, 3)

                const totalResponses = withDelta.reduce((sum, x) => sum + (x.responsesCount || 0), 0)
                const top5Responses = [...withDelta]
                    .sort((a, b) => (b.responsesCount || 0) - (a.responsesCount || 0))
                    .slice(0, 5)
                    .reduce((sum, x) => sum + (x.responsesCount || 0), 0)
                const top5Share = totalResponses > 0 ? (top5Responses / totalResponses) * 100 : 0

                const payload = {
                    mode: this.mode,
                    winners,
                    losers,
                    top5Share,
                    totalResponses,
                    rows: withDelta,
                    loading: this.loading
                }

                this.$emit("overviewUpdated", payload)
            },
            buildDeltaMap() {
                const keyField = this.mode === "url" ? "url" : "domain"
                const oldMap = {}
                this.oldestSources.forEach((s) => {
                    oldMap[s[keyField]] = s
                })
                const map = {}
                this.sources.forEach((s) => {
                    const k = s[keyField]
                    const old = oldMap[k]
                    if (!old) {
                        map[k] = {
                            responsesPct: s.responsesCount ? 100 : 0,
                            itemsPct: s.itemsCount ? 100 : 0,
                            coveragePct: this.mode === "domain" && s.responseCoverage ? 100 : null
                        }
                    } else {
                        map[k] = {
                            responsesPct: this.deltaPct(old.responsesCount, s.responsesCount),
                            itemsPct: this.deltaPct(old.itemsCount, s.itemsCount),
                            coveragePct: this.mode === "domain" ? this.deltaPct(old.responseCoverage, s.responseCoverage) : null
                        }
                    }
                })
                this.deltaMap = map
                this.emitOverviewUpdated()
            },
            async fetchData() {
                if (!this.selectedDates || !this.selectedDates.length) return
                this.loading = true
                const dates = [...this.selectedDates].sort()
                const oldest = dates[0]
                const newest = dates[dates.length - 1]

                let sort = {}
                if (this.options.sortBy && this.options.sortBy.length) {
                    sort[this.options.sortBy[0]] = this.options.sortDesc?.[0] ? -1 : 1
                } else {
                    sort = { responsesCount: -1 }
                }
                const skip = ((this.options.page || 1) - 1) * (this.options.itemsPerPage || this.itemsPerPageDefault)
                const limit = this.options.itemsPerPage || this.itemsPerPageDefault
                const hasPageTypeFilter = this.mode === "url" && !!this.pageTypeFilter
                const requestSkip = this.minimal || hasPageTypeFilter ? 0 : skip
                const requestLimit = this.minimal || hasPageTypeFilter ? 5000 : limit

                const endpointBase = this.mode === "url" ? "/ai-suite/sources/urls/get" : "/ai-suite/sources/get"
                const baseUrl =
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + endpointBase

                const baseCommon = {
                    company_id: localStorage.company_id,
                    project_id: this.project_id || this.$route.params.id,
                    search: this.search,
                    sort,
                    skip: requestSkip,
                    limit: requestLimit
                }
                // Domain-Modus Filter nur dort
                if (this.filters && this.filters.length && this.mode === "domain") baseCommon.filters = this.filters
                // Tags
                if (this.tags && this.tags.length) {
                    baseCommon.tags = this.tags
                    baseCommon.tagsOperator = this.tagsOperator
                }
                // DomainFilter nur im URL-Modus
                if (this.mode === "url" && this.domainFilter) {
                    baseCommon.domainFilter = this.domainFilter
                }

                const isAll = this.selectedPlatform?.key === "all"
                const buildSinglePayload = (singleDate) => ({
                    ...baseCommon,
                    selectedDates: [singleDate],
                    platform: this.selectedPlatform?.key
                })
                const buildMultiPayload = () => {
                    if (!Array.isArray(this.importDatesByPlatform)) return null
                    return {
                        ...baseCommon,
                        platform: null,
                        importDatesByPlatform: this.importDatesByPlatform
                    }
                }

                let promptC = 0
                if (this.project.google) promptC += this.googlePromptCount
                let promptCount = this.project.platforms.length * this.project.keyword_count + promptC

                const computeMetrics = (list, divisor) => {
                    // Nur Domain-Modus Coverage berechnen
                    if (this.mode !== "domain") return
                    list.forEach((x) => {
                        x.responseCoverage = divisor ? x.responsesCount / divisor : x.responsesCount / promptCount
                    })
                }

                try {
                    if (isAll) {
                        const multiPayload = buildMultiPayload()
                        if (!multiPayload) {
                            this.loading = false
                            return
                        }
                        const resp = await this.$helpers.axiosPost(baseUrl, multiPayload)
                        const sourcesByDate = Array.isArray(resp.data?.sourcesByDate) ? resp.data.sourcesByDate : []
                        const olderRaw = Array.isArray(sourcesByDate[0]) ? sourcesByDate[0] : []
                        const newerRaw = Array.isArray(sourcesByDate[1]) ? sourcesByDate[1] : []
                        const divisorOld = resp.data?.totalResponsesByDate?.[0] ?? null
                        const divisorNew = resp.data?.totalResponsesByDate?.[1] ?? null
                        computeMetrics(olderRaw, divisorOld)
                        computeMetrics(newerRaw, divisorNew)
                        const primary = newerRaw && newerRaw.length ? newerRaw : olderRaw
                        const sortKey = this.options.sortBy?.[0] || "responsesCount"
                        const desc = this.options.sortDesc?.[0]
                        primary.sort((a, b) => {
                            const va = a[sortKey] ?? 0
                            const vb = b[sortKey] ?? 0
                            return desc ? vb - va : va - vb
                        })
                        const filteredPrimary = this.filterRowsByPageType(primary)
                        const filteredOldest = this.filterRowsByPageType(olderRaw)
                        const apiTotalAll = Number(resp.data?.total ?? resp.data?.totalCount ?? resp.data?.count)
                        this.total = hasPageTypeFilter ? filteredPrimary.length : Number.isFinite(apiTotalAll) && apiTotalAll > 0 ? apiTotalAll : filteredPrimary.length
                        this.oldestSources = filteredOldest
                        if (this.minimal) {
                            this.sources = filteredPrimary
                        } else if (hasPageTypeFilter) {
                            this.sources = filteredPrimary.slice(skip, skip + limit)
                        } else {
                            // In diesem Pfad liefert das Backend bereits die gewünschte Seite.
                            this.sources = filteredPrimary
                        }
                        this.buildDeltaMap()
                        this.sortDisplayed()
                    } else {
                        const [latestResp, oldestResp] = await Promise.all([this.$helpers.axiosPost(baseUrl, buildSinglePayload(newest)), this.$helpers.axiosPost(baseUrl, buildSinglePayload(oldest))])
                        const divisorNew = latestResp.data.totalResponsesByDate
                        const divisorOld = oldestResp.data.totalResponsesByDate
                        const newestList = Array.isArray(latestResp.data.sources) ? latestResp.data.sources : []
                        const oldestList = Array.isArray(oldestResp.data.sources) ? oldestResp.data.sources : []
                        computeMetrics(newestList, divisorNew)
                        computeMetrics(oldestList, divisorOld)
                        const filteredNewest = this.filterRowsByPageType(newestList)
                        const filteredOldest = this.filterRowsByPageType(oldestList)
                        if (this.minimal) {
                            this.sources = filteredNewest
                        } else if (hasPageTypeFilter) {
                            this.sources = filteredNewest.slice(skip, skip + limit)
                        } else {
                            // In diesem Pfad liefert das Backend bereits die gewünschte Seite.
                            this.sources = filteredNewest
                        }
                        const apiTotal = Number(latestResp.data?.total ?? latestResp.data?.totalCount ?? latestResp.data?.count)
                        const inferredBase = this.minimal ? filteredNewest.length : (this.options.page - 1) * limit + filteredNewest.length
                        const inferredTotal = !this.minimal && filteredNewest.length === limit ? inferredBase + 1 : inferredBase
                        this.total = hasPageTypeFilter ? filteredNewest.length : Number.isFinite(apiTotal) && apiTotal > 0 ? apiTotal : Math.max(filteredNewest.length, inferredTotal)
                        this.oldestSources = filteredOldest
                        this.buildDeltaMap()
                        this.sortDisplayed()
                    }
                } finally {
                    this.loading = false
                    this._needsRefreshOnActivate = false
                }
            },
            onModeToggle() {
                // Beim manuellen Wechsel aus URL-Drilldown zurück zu Domains Filter löschen
                if (this.mode === "domain" && this.domainFilter) {
                    this.domainFilter = null
                }
                this.resetAndFetch()
            },
            onRowClick(row) {
                if (this.mode === "domain" && row?.domain) {
                    this.domainFilter = row.domain
                    this.setMode("url")
                    // Seite zurücksetzen (URLs neu)
                    this.options = { ...this.options, page: 1 }
                    this.fetchData()
                    return
                }
                if (this.mode === "url" && row?.url) {
                    this.openUrlPanel(row)
                }
            },
            clearDomainFilter() {
                this.domainFilter = null
                this.setMode("domain")
            },
            getResponseEntities(resp) {
                const sourceList = Array.isArray(resp && resp.mergedEntities) && resp.mergedEntities.length ? resp.mergedEntities : Array.isArray(resp && resp.entities) ? resp.entities : []
                const labels = []
                const seen = new Set()

                sourceList.forEach((entity) => {
                    const raw = (entity && (entity.canonicalName || entity.name)) || ""
                    const label = raw && typeof raw === "string" ? raw.trim() : ""
                    if (!label) return
                    const key = label.toLowerCase()
                    if (seen.has(key)) return
                    seen.add(key)
                    labels.push({
                        label,
                        color: getBrandColorFromName(key)
                    })
                })

                return labels
            },
            entityDotStyle(entity) {
                return {
                    backgroundColor: entity?.color || "#94a3b8"
                }
            },
            entityChipStyle(entity) {
                const color = entity?.color || "#94a3b8"
                return {
                    borderColor: `${color}66`,
                    backgroundColor: `${color}10`
                }
            },
            normalizeUrlForOpen(url) {
                const raw = (url || "").toString().trim()
                if (!raw) return "#"
                if (/^https?:\/\//i.test(raw)) return raw
                return `https://${raw}`
            },
            getPlatformInfo(name) {
                let cursor = this.$parent
                while (cursor) {
                    if (typeof cursor.getPlatformInfo === "function") {
                        try {
                            const info = cursor.getPlatformInfo(name)
                            if (info && (info.logo || info.label)) return info
                        } catch (e) {
                            // ignore and try next parent
                        }
                    }
                    cursor = cursor.$parent
                }

                const key = (name || "").toString().trim().toLowerCase()
                const googleLogo = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/></svg>`
                const logos = {
                    google: googleLogo,
                    google_ai_mode: googleLogo,
                    grok: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path d="M18.542 30.532l15.956-11.776c.783-.576 1.902-.354 2.274.545 1.962 4.728 1.084 10.411-2.819 14.315-3.903 3.901-9.333 4.756-14.299 2.808l-5.423 2.511c7.778 5.315 17.224 4 23.125-1.903 4.682-4.679 6.131-11.058 4.775-16.812l.011.011c-1.966-8.452.482-11.829 5.501-18.735C47.759 1.332 47.88 1.166 48 1l-6.602 6.599V7.577l-22.86 22.958M15.248 33.392c-5.582-5.329-4.619-13.579.142-18.339 3.521-3.522 9.294-4.958 14.331-2.847l5.412-2.497c-.974-.704-2.224-1.46-3.659-1.994-6.478-2.666-14.238-1.34-19.505 3.922C6.904 16.701 5.31 24.488 8.045 31.133c2.044 4.965-1.307 8.48-4.682 12.023C2.164 44.411.967 45.67 0 47l15.241-13.608"/></svg>`,
                    perplexity: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 509.64"><path fill="#1F1F1F" d="M115.613 0h280.774C459.974 0 512 52.025 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.613C52.026 509.64 0 457.614 0 394.027V115.612C0 52.025 52.026 0 115.613 0z"/><path fill="#fff" d="M348.851 128.063l-68.946 58.302h68.946v-58.302zm-83.908 48.709l100.931-85.349v94.942h32.244v143.421h-38.731v90.004l-94.442-86.662v83.946h-17.023v-83.906l-96.596 86.246v-89.628h-37.445V186.365h38.732V90.768l95.309 84.958v-83.16h17.023l-.002 84.206z"/></svg>`,
                    claude: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><circle cx="12" cy="12" r="11" fill="#D77655"/><text x="12" y="16" text-anchor="middle" font-size="11" font-weight="700" fill="#fff">C</text></svg>`,
                    gemini: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="#7c6cff" d="M12 2l1.8 4.8L18.6 8.6l-4.8 1.8L12 15.2l-1.8-4.8L5.4 8.6l4.8-1.8z"/></svg>`,
                    openai: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><circle cx="12" cy="12" r="11" fill="#111"/><text x="12" y="16" text-anchor="middle" font-size="10" font-weight="700" fill="#fff">AI</text></svg>`,
                    chatgpt: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><circle cx="12" cy="12" r="11" fill="#111"/><text x="12" y="16" text-anchor="middle" font-size="8" font-weight="700" fill="#fff">GPT</text></svg>`
                }

                const labels = {
                    openai: "OpenAI",
                    chatgpt: "ChatGPT",
                    google: "Google AIO",
                    google_ai_mode: "AI Mode",
                    gemini: "Gemini",
                    claude: "Claude",
                    perplexity: "Perplexity",
                    grok: "Grok"
                }

                return {
                    logo: logos[key] || null,
                    label: labels[key] || name || "—"
                }
            },
            async openUrlPanel(row) {
                if (!row || !row.url) return
                this.selectedUrlRow = row
                this.urlPanelOpen = true
                this.urlPanelLoading = true
                this.urlPanelResponses = []

                const baseUrl =
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/promptresponses/get"

                const payload = {
                    company_id: localStorage.company_id,
                    project_id: this.project_id || this.$route.params.id,
                    selectedDates: this.selectedDates,
                    sort: { importDate: -1 },
                    skip: 0,
                    limit: 100,
                    filters: [
                        {
                            field: { key: "sources" },
                            operator: { value: "contains" },
                            value: row.url
                        }
                    ]
                }

                if (this.selectedPlatform && this.selectedPlatform.key && this.selectedPlatform.key !== "all") {
                    payload.platform = this.selectedPlatform.key
                }
                if (this.tags && this.tags.length) {
                    payload.tags = this.tags
                    payload.tagsOperator = this.tagsOperator
                }

                try {
                    const resp = await this.$helpers.axiosPost(baseUrl, payload)
                    this.urlPanelResponses = Array.isArray(resp?.data?.responses) ? resp.data.responses : []
                } catch (e) {
                    this.urlPanelResponses = []
                } finally {
                    this.urlPanelLoading = false
                }
            },
            openPromptDetail(item) {
                if (!item) return
                const projectId = this.project?._id || this.project_id || this.$route?.params?.id
                if (!projectId) return

                const query = {}
                if (item.task_id || item.taskId) query.task_id = item.task_id || item.taskId
                if (item.prompt) query.prompt = item.prompt
                if (item.platform) query.platform = item.platform
                const routeBrand = this.getRouteBrandPayload()
                if (routeBrand) query.brand = routeBrand

                const dateRange = Array.isArray(this.selectedDates) ? this.selectedDates.filter(Boolean) : []
                if (dateRange.length) {
                    query.dates = JSON.stringify(dateRange)
                }

                // Bevorzugt als Overlay im AIProject öffnen (kein Routenwechsel, View bleibt erhalten)
                if (this.aiSuiteOpenPromptDetail) {
                    this.aiSuiteOpenPromptDetail({ ...query, project_id: projectId })
                    return
                }

                this.$router.push({
                    name: "PromptDetail",
                    params: { id: projectId },
                    query
                })
            },
            normalizeToken(value) {
                return (value || "")
                    .toString()
                    .normalize("NFKD")
                    .replace(/[\u2018\u2019\u0060\u00B4]/g, "'")
                    .replace(/['’`´]/g, "")
                    .replace(/\s+/g, " ")
                    .trim()
                    .toLowerCase()
            },
            findMatchedBrandByProjectName() {
                const projectBrandName = this.project?.brand?.brand_name || ""
                const projectToken = this.normalizeToken(projectBrandName)
                if (!projectToken || !Array.isArray(this.brands) || !this.brands.length) return null

                const getMentionValue = (mention) => {
                    if (mention == null) return ""
                    if (typeof mention === "string" || typeof mention === "number") return String(mention)
                    return mention?.text?.content || mention?.content || mention?.canonicalName || mention?.name || mention?.brand || mention?.entity || ""
                }

                return (
                    this.brands.find((entry) => {
                        const mentions = Array.isArray(entry?.mentions) ? entry.mentions : []
                        return mentions.some((mention) => this.normalizeToken(getMentionValue(mention)) === projectToken)
                    }) || null
                )
            },
            getRouteBrandPayload() {
                const matched = this.findMatchedBrandByProjectName()
                if (!matched) return null

                try {
                    return JSON.stringify({
                        canonicalName: matched.canonicalName || matched.brand_name || matched.brand || matched.name || null,
                        brand_name: matched.brand_name || matched.brand || matched.canonicalName || matched.name || null,
                        brand: matched.brand || matched.brand_name || matched.canonicalName || matched.name || null,
                        mentions: Array.isArray(matched.mentions) ? matched.mentions : []
                    })
                } catch (e) {
                    return null
                }
            }
            // ...existing methods...
        }
    }
</script>

<style>
    .right-side-dialog {
        margin: 0 !important;
        margin-left: auto !important;
        height: 100vh;
        max-height: 100vh;
        display: flex;
        align-items: stretch;
    }
    .right-side-dialog .v-card {
        width: 560px;
        height: 100vh;
        max-height: 100vh;
        overflow-y: auto;
        border-radius: 0;
    }
    .url-panel-card {
        border: 1px solid rgba(15, 23, 42, 0.08);
        box-shadow: 0 18px 42px -28px rgba(2, 6, 23, 0.7);
    }
    .url-panel-toolbar {
        border-bottom: 1px solid rgba(15, 23, 42, 0.08);
        background: linear-gradient(180deg, #f8fafc, #f1f5f9);
    }
    .url-panel-title-wrap {
        min-width: 0;
    }
    .url-panel-title {
        font-size: 15px;
        font-weight: 700;
        color: #0f172a;
        line-height: 1.1;
    }
    .url-panel-subtitle {
        margin-top: 3px;
        font-size: 11px;
        font-weight: 500;
        color: #64748b;
        line-height: 1.25;
    }
    .url-panel-meta {
        padding: 8px 10px;
        border-radius: 10px;
        border: 1px solid rgba(15, 23, 42, 0.08);
        background: rgba(15, 23, 42, 0.03);
    }
    .url-panel-url-row {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
    }
    .url-panel-favicon-wrap {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: #fff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        flex: 0 0 auto;
        border: 1px solid rgba(15, 23, 42, 0.08);
    }
    .url-panel-favicon {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        object-fit: cover;
    }
    .url-panel-favicon-fallback {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 700;
        color: #2563eb;
    }
    .url-panel-url {
        font-size: 12px;
        line-height: 1.35;
        font-weight: 600;
        word-break: break-all;
        color: #1f2937;
        text-decoration: none;
        min-width: 0;
    }
    .url-panel-url:hover {
        text-decoration: underline;
    }
    .url-panel-domain {
        margin-top: 4px;
        font-size: 11px;
        color: #64748b;
    }
    .url-panel-list {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 8px;
    }
    .url-panel-item {
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 10px;
        padding: 8px 10px;
        background: #fff;
        cursor: pointer;
        min-width: 0;
        transition: border-color 0.16s ease, background 0.16s ease;
    }
    .url-panel-item:hover {
        border-color: rgba(59, 130, 246, 0.35);
        background: rgba(59, 130, 246, 0.05);
    }
    .url-panel-item__head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 5px;
    }
    .url-panel-item__platform-wrap {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
    }
    .url-panel-platform-icon-wrapper {
        width: 18px;
        height: 18px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background: rgba(15, 23, 42, 0.06);
        overflow: hidden;
        flex: 0 0 auto;
    }
    .url-panel-platform-icon {
        width: 14px;
        height: 14px;
        display: inline-flex;
    }
    .url-panel-platform-icon svg {
        width: 100%;
        height: 100%;
        display: block;
    }
    .url-panel-item__entities {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        margin-bottom: 6px;
    }
    .url-entity-chip {
        border-color: rgba(30, 41, 59, 0.18) !important;
        color: #334155 !important;
        background: rgba(15, 23, 42, 0.04) !important;
        font-weight: 600;
    }
    .entity-chip-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 6px;
        display: inline-block;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
    }
    .url-entity-chip-more {
        border-color: rgba(100, 116, 139, 0.3) !important;
        color: #64748b !important;
        background: rgba(148, 163, 184, 0.14) !important;
        font-weight: 600;
    }
    .url-entity-tooltip-list {
        display: grid;
        gap: 6px;
        font-size: 12px;
    }
    .url-entity-tooltip-row {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }
    .url-panel-item__platform {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.35px;
        color: #475569;
    }
    .url-panel-item__date {
        font-size: 11px;
        color: #64748b;
    }
    .url-panel-item__prompt {
        font-size: 12px;
        overflow-wrap: anywhere;
        word-break: break-word;
        font-weight: 600;
        color: #1e293b;
        margin-bottom: 3px;
        display: -webkit-box;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
    .url-panel-item__response {
        font-size: 12px;
        overflow-wrap: anywhere;
        word-break: break-word;
        color: #475569;
        display: -webkit-box;
        line-clamp: 4;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
    .theme--dark .url-panel-meta {
        border-color: rgba(255, 255, 255, 0.12);
        background: rgba(148, 163, 184, 0.08);
    }
    .theme--dark .url-panel-card {
        border-color: rgba(255, 255, 255, 0.12);
        box-shadow: 0 18px 42px -24px rgba(0, 0, 0, 0.75);
    }
    .theme--dark .url-panel-toolbar {
        border-bottom-color: rgba(255, 255, 255, 0.12);
        background: linear-gradient(180deg, #22272b, #1c2125);
    }
    .theme--dark .url-panel-title {
        color: #e2e8f0;
    }
    .theme--dark .url-panel-subtitle {
        color: #94a3b8;
    }
    .theme--dark .url-panel-url {
        color: #e2e8f0;
    }
    .theme--dark .url-panel-favicon-wrap {
        background: #1f252b;
        border-color: rgba(255, 255, 255, 0.12);
    }
    .theme--dark .url-panel-favicon-fallback {
        color: #93c5fd;
    }
    .theme--dark .url-panel-domain,
    .theme--dark .url-panel-item__date {
        color: #94a3b8;
    }
    .theme--dark .url-panel-item {
        border-color: rgba(255, 255, 255, 0.12);
        background: #1f252b;
    }
    .theme--dark .url-panel-platform-icon-wrapper {
        background: rgba(148, 163, 184, 0.14);
    }
    .theme--dark .url-panel-item:hover {
        border-color: rgba(96, 165, 250, 0.45);
        background: rgba(96, 165, 250, 0.14);
    }
    .theme--dark .url-panel-item__platform {
        color: #cbd5e1;
    }
    .theme--dark .url-entity-chip {
        border-color: rgba(148, 163, 184, 0.35) !important;
        color: #cbd5e1 !important;
        background: rgba(148, 163, 184, 0.12) !important;
    }
    .theme--dark .url-entity-chip-more {
        border-color: rgba(148, 163, 184, 0.35) !important;
        color: #aab6c5 !important;
        background: rgba(148, 163, 184, 0.1) !important;
    }
    .theme--dark .url-panel-item__prompt {
        color: #e2e8f0;
    }
    .theme--dark .url-panel-item__response {
        color: #cbd5e1;
    }

    .sources-filter-row {
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }
    .theme--dark .sources-filter-row {
        border-bottom-color: rgba(255, 255, 255, 0.08);
    }
    .sources-mode-toggle {
        border-radius: 999px;
        overflow: hidden;
        background: transparent;
    }
    .sources-mode-toggle .v-btn.sources-mode-btn {
        min-width: 120px !important;
        text-transform: none;
        letter-spacing: 0.15px;
        font-size: 12.5px;
        font-weight: 600;
        color: #66707d !important;
        border-radius: 999px !important;
        padding: 0 14px !important;
        transition: background 0.2s, color 0.2s;
    }
    .sources-mode-toggle .v-btn.sources-mode-btn .v-icon {
        color: inherit !important;
        opacity: 0.85;
    }
    .sources-mode-toggle .v-btn.sources-mode-btn.v-btn--active {
        background: #ffffff !important;
        color: var(--v-primary-base) !important;
        /* box-shadow: 0 1px 3px rgba(0, 0, 0, 0.16); */
    }
    .theme--dark .sources-mode-toggle .v-btn.sources-mode-btn {
        color: #b7c0cb !important;
    }
    .theme--dark .sources-mode-toggle .v-btn.sources-mode-btn.v-btn--active {
        background: #1e2328 !important;
        color: #73b6ff !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
    }
    .sources-mode-toggle .v-btn.sources-mode-btn:not(.v-btn--active):hover {
        background: rgba(255, 255, 255, 0.55) !important;
    }
    .theme--dark .sources-mode-toggle .v-btn.sources-mode-btn:not(.v-btn--active):hover {
        background: rgba(255, 255, 255, 0.06) !important;
    }
    .sources-mode-right {
        display: inline-flex;
        align-items: center;
        min-width: 0;
    }
    .sources-domain-chip {
        font-weight: 600;
        letter-spacing: 0.15px;
        border-radius: 999px;
        max-width: 360px;
    }

    #monitoringSourcesTable th {
        white-space: nowrap !important;
        text-transform: uppercase !important;
        font-weight: 300 !important;
    }
    .domain-cell {
        display: flex;
        align-items: center;
        gap: 6px;
        max-width: 260px;
        overflow: hidden;
    }
    .domain-cell .fav {
        width: 20px;
        height: 20px;
        border-radius: 4px;
        flex-shrink: 0;
    }
    .domain-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 500;
    }
    .sample-link {
        color: #1976d2;
        text-decoration: none;
        font-size: 0.85rem;
        display: inline-block;
        max-width: 260px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .sample-link:hover {
        text-decoration: underline;
    }
    .mono {
        font-weight: 600;
        font-size: 13px;
        letter-spacing: 0.3px;
    }
    .v-data-table .v-data-table__wrapper td {
        padding-top: 10px !important;
        padding-bottom: 10px !important;
    }
    .hdr-wrapper {
        display: inline-flex;
        align-items: center;
        text-transform: uppercase !important;
    }
    .hdr-label {
        display: inline-flex;
        align-items: center;
        text-transform: uppercase !important;
    }
    .hdr-info {
        cursor: pointer;
    }
    .sources-sort-mode-btn {
        width: 22px;
        height: 22px;
        min-width: 22px;
        border-radius: 7px !important;
        background: rgba(15, 23, 42, 0.08) !important;
        border: 1px solid rgba(15, 23, 42, 0.14) !important;
        color: #72808f !important;
        transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease;
    }
    .sources-sort-mode-btn:hover {
        background: rgba(15, 23, 42, 0.13) !important;
        border-color: rgba(15, 23, 42, 0.2) !important;
    }
    .sources-sort-mode-btn.active {
        background: rgba(15, 118, 110, 0.14) !important;
        border-color: rgba(15, 118, 110, 0.32) !important;
        color: #0f766e !important;
    }
    .theme--dark .sources-sort-mode-btn {
        background: rgba(148, 163, 184, 0.14) !important;
        border-color: rgba(148, 163, 184, 0.28) !important;
        color: #94a3b8 !important;
    }
    .theme--dark .sources-sort-mode-btn:hover {
        background: rgba(148, 163, 184, 0.24) !important;
        border-color: rgba(148, 163, 184, 0.36) !important;
    }
    .theme--dark .sources-sort-mode-btn.active {
        background: rgba(20, 184, 166, 0.18) !important;
        border-color: rgba(20, 184, 166, 0.36) !important;
        color: #5eead4 !important;
    }
    .fav_wrapper {
        width: 32px;
        height: 32px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .fav_wrapper .fav {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: #fff;
        object-fit: cover;
    }
    .domain-placeholder {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 0.9rem;
        background: #fff;
        color: #219653;
    }
    .sources-card {
        border-radius: 16px;
        overflow: hidden;
    }
    .theme--dark .sources-card {
        background: linear-gradient(145deg, #1f2123, #262a2d);
    }
    .sources-actionbar {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        border-bottom: 1px solid rgba(0, 0, 0, 0.04);
        background: linear-gradient(180deg, #f7f9fa, #f1f3f5);
    }
    .theme--dark .sources-actionbar {
        border-bottom-color: rgba(255, 255, 255, 0.07);
        background: linear-gradient(180deg, #26292c, #202325);
    }
    .sab-title {
        font-size: 16px;
        font-weight: 600;
        margin: 0 0 2px;
        letter-spacing: 0.3px;
    }
    .sab-sub {
        font-size: 12px;
        font-weight: 500;
        opacity: 0.75;
        display: flex;
        align-items: center;
    }
    .sab-platform {
        text-transform: none;
        font-weight: 600;
        letter-spacing: 0.3px;
        background: #fff !important;
    }
    .theme--dark .sab-platform {
        background: #1d1f21 !important;
    }

    /* Headers */
    .sources-table thead th {
        /* background: linear-gradient(180deg, #f5f7f9, #eef1f4) !important; */
        font-size: 11px;
        letter-spacing: 0.55px;
        text-transform: uppercase;
        font-weight: 600 !important;
        color: #56606a;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
    }
    .theme--dark .sources-table thead th {
        background: #232628 !important;
        color: #b7c0c7;
        border-bottom-color: rgba(255, 255, 255, 0.08) !important;
    }
    .hdr-wrapper {
        display: inline-flex;
        align-items: center;
        position: relative;
    }
    .hdr-label {
        display: inline-flex;
        align-items: center;
        line-height: 1.1;
    }
    .filter-btn {
        opacity: 0.55;
        transition: opacity 0.2s;
    }
    .filter-btn:hover {
        opacity: 1;
    }

    /* Rows */
    .sources-table tbody tr {
        transition: background 0.18s, box-shadow 0.18s;
    }
    .sources-table tbody tr:hover {
        background: #f3f7fb;
    }
    .theme--dark .sources-table tbody tr:hover {
        background: #293034;
    }
    .sources-table tbody tr:nth-child(even) {
        background: #fcfdfe;
    }
    .theme--dark .sources-table tbody tr:nth-child(even) {
        background: #212528;
    }
    .sources-table tbody tr:hover:nth-child(even) {
        background: #eef4f9;
    }
    .theme--dark .sources-table tbody tr:hover:nth-child(even) {
        background: #2b3236;
    }

    /* Domain cell */
    .domain-cell {
        display: flex;
        align-items: center;
        min-width: 0;
        padding: 6px 0;
    }
    .fav_wrapper {
        width: 40px;
        height: 40px;
        border-radius: 12px;
        background: linear-gradient(135deg, #eef2f5, #ffffff);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 12px;
        flex-shrink: 0;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        overflow: hidden;
        position: relative;
    }
    .theme--dark .fav_wrapper {
        background: linear-gradient(135deg, #2a2d31, #1f2123);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.45);
    }
    .fav_wrapper.fail {
        background: linear-gradient(135deg, #e0e4e8, #f9fafb);
    }
    .domain-placeholder {
        width: 100%;
        height: 100%;
        font-weight: 600;
        font-size: 14px;
        color: #2196f3;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .theme--dark .domain-placeholder {
        color: #64b5f6;
    }
    .fav_wrapper .fav {
        width: 26px;
        height: 26px;
        border-radius: 6px;
        object-fit: cover;
    }
    .domain-meta {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }
    .domain-text {
        font-weight: 600;
        font-size: 14px;
        color: #1d2226;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .theme--dark .domain-text {
        color: #e6ecef;
    }
    .domain-secondary {
        font-size: 11px;
        opacity: 0.8;
        margin-top: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    /* Numeric cells */
    .num {
        font-weight: 600;
        font-size: 12.5px;
        letter-spacing: 0.3px;
    }

    .coverage-cell {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 190px;
    }

    .cov-bar {
        position: relative;
        height: 8px;
        width: 120px;
        background: linear-gradient(90deg, #e3e8ec, #f1f4f6);
        border-radius: 5px;
        overflow: hidden;
    }
    .theme--dark .cov-bar {
        background: linear-gradient(90deg, #2b3034, #202428);
    }
    .cov-bar-fill {
        position: absolute;
        inset: 0;
        /* Fallback (einfarbig) */
        background: var(--v-primary-base);
        /* Gradient mit Aufhellungen der Primary */
        background: linear-gradient(90deg, var(--v-primary-base) 0%, color-mix(in srgb, var(--v-primary-base) 70%, #ffffff) 55%, color-mix(in srgb, var(--v-primary-base) 40%, #ffffff) 100%);
        transition: width 0.5s cubic-bezier(0.55, 0.12, 0.35, 1);
    }

    .theme--dark .cov-bar-fill {
        background: var(--v-primary-base);
        background: linear-gradient(
            90deg,
            color-mix(in srgb, var(--v-primary-base) 90%, #000000) 0%,
            color-mix(in srgb, var(--v-primary-base) 65%, #000000) 55%,
            color-mix(in srgb, var(--v-primary-base) 45%, #000000) 100%
        );
    }
    .cov-bar-fill::after {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.35), rgba(255, 255, 255, 0) 60%);
        mix-blend-mode: screen;
        opacity: 0.55;
        pointer-events: none;
    }
    .theme--dark .cov-bar-fill::after {
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0) 70%);
        opacity: 0.6;
    }
    .cov-val {
        opacity: 0.85;
    }

    .metric-chip {
        display: inline-flex;
        align-items: center;
        padding: 4px 10px 3px;
        background: #eef2f6;
        border-radius: 8px;
        min-width: 72px;
        justify-content: center;
        font-weight: 600;
        line-height: 1;
        position: relative;
    }
    .metric-chip.alt {
        background: #e9eef2;
    }
    .metric-chip.subtle {
        background: #f3f6f9;
    }
    .theme--dark .metric-chip {
        background: #2a3136;
    }
    .theme--dark .metric-chip.alt {
        background: #273037;
    }
    .theme--dark .metric-chip.subtle {
        background: #30373d;
    }
    .metric-number {
        font-size: 12.5px;
        letter-spacing: 0.3px;
    }

    .date-cell {
        font-size: 11px;
        font-weight: 600;
        opacity: 0.65;
        letter-spacing: 0.4px;
    }

    /* Hover Highlight für Zahlen */
    .sources-table tbody tr:hover .metric-chip {
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.04);
    }
    .theme--dark .sources-table tbody tr:hover .metric-chip {
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.06);
    }

    /* Optional schmalere Zelle für Updated */
    #monitoringSourcesTable td:nth-last-child(1) {
        white-space: nowrap;
    }

    /* Delta Chips für Prozentveränderungen */
    .delta-badge {
        font-size: 13px;
        /* font-weight: 600; */
        padding: 5px 10px 4px 8px;
        border-radius: 18px;
        background: #eef2f5;
        color: #3a4751;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-variant-numeric: tabular-nums;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
        transition: background 0.25s, color 0.25s;
    }
    .theme--dark .delta-badge {
        background: #343a40;
        color: #ccd3d9;
    }
    .delta-badge.up {
        background: rgba(30, 150, 70, 0.14);
        color: #1e7f34;
    }
    .theme--dark .delta-badge.up {
        background: rgba(30, 150, 70, 0.22);
        color: #6fdc91;
    }
    .delta-badge.down {
        background: rgba(200, 60, 50, 0.14);
        color: #c43d35;
    }
    .theme--dark .delta-badge.down {
        background: rgba(200, 60, 50, 0.22);
        color: #ff9c92;
    }

    .sources-mini-footer {
        border-top: 1px solid rgba(0, 0, 0, 0.06);
        /* background: linear-gradient(180deg, #fafbfc, #f3f5f7); */
        font-size: 12px;
        font-weight: 500;
    }
    .theme--dark .sources-mini-footer {
        background: #22272a;
        border-top-color: rgba(255, 255, 255, 0.08);
    }
    .sources-mini-footer .mini-status {
        font-size: 12px;
        opacity: 0.8;
    }
    .sources-mini-footer .mini-status .sep {
        margin: 0 6px;
        opacity: 0.5;
    }
    .domain-cell.mini {
        padding: 2px 0;
    }
    .metric-chip.mini-chip {
        background: transparent;
        padding: 0;
        min-width: auto;
    }
    .metric-chip.mini-chip .metric-number {
        font-size: 12.5px;
    }

    .metric-wrapper {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }
    .page-type-chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        max-width: 220px;
        padding: 4px 10px;
        border-radius: 999px;
        border: 1px solid rgba(148, 163, 184, 0.45);
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.2px;
        color: #334155;
        background: rgba(15, 23, 42, 0.08);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .page-type-chip__dot {
        width: 8px;
        height: 8px;
        border-radius: 999px;
        flex: 0 0 auto;
    }
    .page-type-chip__label {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .page-type-chip.empty {
        color: #94a3b8;
        background: rgba(148, 163, 184, 0.14);
    }
    .theme--dark .page-type-chip {
        color: #cbd5e1;
        background: rgba(148, 163, 184, 0.18);
    }
    .theme--dark .page-type-chip.empty {
        color: #94a3b8;
        background: rgba(148, 163, 184, 0.12);
    }
    .metric-value {
        font-weight: 600;
        font-size: 13px;
        letter-spacing: 0.3px;
        font-variant-numeric: tabular-nums;
    }
    .delta-chip {
        font-size: 14px;
        padding: 5px 10px 4px 8px;
        border-radius: 18px;
        background: #eef2f5;
        color: #3a4751;
        display: inline-flex;
        align-items: center;
        font-variant-numeric: tabular-nums;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
        transition: background 0.25s, color 0.25s;
    }
    .theme--dark .delta-chip {
        background: #343a40;
        color: #ccd3d9;
    }
    .delta-chip.up {
        background: rgba(30, 150, 70, 0.14);
        color: #1e7f34;
    }
    .theme--dark .delta-chip.up {
        background: rgba(30, 150, 70, 0.22);
        color: #6fdc91;
    }
    .delta-chip.down {
        background: rgba(200, 60, 50, 0.14);
        color: #c43d35;
    }
    .theme--dark .delta-chip.down {
        background: rgba(200, 60, 50, 0.22);
        color: #ff9c92;
    }
    .delta-chip .v-icon {
        margin-right: 2px;
    }

    /* Nach bestehendem CSS anhängen */
    .domain-cell.url-mode {
        max-width: none;
    }
    .url-text {
        white-space: normal;
        word-break: break-all;
        line-height: 1.2;
        font-size: 14px;
        font-weight: bold;
    }

    .top-sources-panel {
        border-top: 1px solid rgba(0, 0, 0, 0.06);
        border-radius: 12px;
    }
    .theme--dark .top-sources-panel {
        border-top-color: rgba(255, 255, 255, 0.08);
        background: #1f2328;
    }
    .ts-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        padding: 8px 10px;
        border-radius: 10px;
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-left: 3px solid rgba(15, 23, 42, 0.08);
        background: #fff;
        margin-bottom: 8px;
    }
    .theme--dark .ts-row {
        background: #242a31;
        border-color: rgba(255, 255, 255, 0.1);
        border-left-color: rgba(255, 255, 255, 0.12);
    }
    .ts-row.clickable {
        cursor: pointer;
        transition: background 0.18s, border-color 0.18s, transform 0.12s, box-shadow 0.18s;
    }
    .ts-row.clickable:hover {
        background: color-mix(in srgb, var(--v-primary-base) 10%, #ffffff);
        border-color: color-mix(in srgb, var(--v-primary-base) 36%, #d7e2ee);
        border-left-color: color-mix(in srgb, var(--v-primary-base) 70%, #ffffff);
        transform: translateY(-1px);
        box-shadow: 0 6px 14px -10px rgba(0, 0, 0, 0.35);
    }
    .theme--dark .ts-row.clickable:hover {
        background: color-mix(in srgb, var(--v-primary-base) 22%, #1f2328);
        border-color: color-mix(in srgb, var(--v-primary-base) 42%, rgba(255, 255, 255, 0.14));
        border-left-color: color-mix(in srgb, var(--v-primary-base) 74%, #1f2328);
        box-shadow: 0 8px 16px -10px rgba(0, 0, 0, 0.7);
    }
    .ts-left {
        display: flex;
        align-items: center;
        min-width: 0;
        gap: 10px;
    }
    .ts-name-wrap {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }
    .ts-name {
        font-size: 14px;
        font-weight: 600;
        color: #333b46;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 210px;
        line-height: 1.3;
    }
    .theme--dark .ts-name {
        color: #e6ebf2;
    }
    .ts-meta {
        font-size: 11px;
        color: #8a93a1;
        font-weight: 500;
        letter-spacing: 0.1px;
    }
    .theme--dark .ts-meta {
        color: #9ca7b5;
    }
    .ts-hover-hint {
        display: inline-flex;
        align-items: center;
        margin-top: 2px;
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.15px;
        color: color-mix(in srgb, var(--v-primary-base) 72%, #6b7280);
        opacity: 0;
        max-height: 0;
        transform: translateY(-2px);
        transition: opacity 0.16s, transform 0.16s, max-height 0.16s;
        pointer-events: none;
    }
    .ts-row.clickable:hover .ts-hover-hint {
        opacity: 1;
        max-height: 18px;
        transform: translateY(0);
    }
    .theme--dark .ts-hover-hint {
        color: color-mix(in srgb, var(--v-primary-base) 76%, #cfd6de);
    }
    .ts-right {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
    }
    .ts-value {
        font-size: 18px;
        font-weight: 700;
        line-height: 1;
        color: #1f2937;
        font-variant-numeric: tabular-nums;
    }
    .theme--dark .ts-value {
        color: #f3f6fb;
    }
    .ts-delta {
        font-size: 12px;
        font-weight: 700;
        padding: 2px 8px;
        border-radius: 999px;
        background: rgba(107, 114, 128, 0.14);
        color: #6b7280;
        display: inline-flex;
        align-items: center;
        font-variant-numeric: tabular-nums;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
        transition: background 0.25s, color 0.25s;
    }
    .theme--dark .ts-delta {
        background: #343a40;
        color: #ccd3d9;
    }
    .ts-delta.up {
        background: rgba(30, 150, 70, 0.15);
        color: #1c8c45;
    }
    .ts-delta.down {
        background: rgba(214, 69, 69, 0.15);
        color: #cf3d3d;
    }
    .theme--dark .ts-delta.up {
        background: rgba(30, 150, 70, 0.22);
        color: #6fdc91;
    }
    .theme--dark .ts-delta.down {
        background: rgba(200, 60, 50, 0.22);
        color: #ff9c92;
    }
    .ts-divider-label {
        margin: 10px 4px 8px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        opacity: 0.68;
    }
    .ts-pagination-wrap {
        margin-top: 2px;
        margin-bottom: 4px;
        border-top: 1px dashed rgba(0, 0, 0, 0.12);
        padding-top: 6px;
    }
    .ts-pagination-status {
        font-size: 12px;
        color: #7f8792;
        font-weight: 600;
    }
    .theme--dark .ts-pagination-wrap {
        border-top-color: rgba(255, 255, 255, 0.16);
    }
    .theme--dark .ts-pagination-status {
        color: #a4adba;
    }
    .ts-empty {
        padding: 14px 10px;
        text-align: center;
        font-size: 12px;
        opacity: 0.7;
    }

    .rows-clickable tbody tr {
        cursor: pointer;
    }
    /* optional, explizit beim Hover */
    .rows-clickable tbody tr:hover {
        cursor: pointer;
    }

    .src-kpi {
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 10px;
        background: rgba(255, 255, 255, 0.7);
        padding: 10px;
        min-height: 118px;
    }
    .src-kpi__label {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.4px;
        text-transform: uppercase;
        color: rgba(15, 23, 42, 0.62);
        margin-bottom: 6px;
    }
    .src-kpi__value {
        font-size: 24px;
        font-weight: 700;
        line-height: 1.1;
        margin-bottom: 4px;
    }
    .src-kpi__hint {
        font-size: 12px;
        color: rgba(55, 65, 81, 0.75);
    }
    .src-list {
        display: grid;
        gap: 6px;
    }
    .src-list__row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
    }
    .src-list__name {
        font-size: 12px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .src-list__delta {
        font-size: 12px;
        font-weight: 700;
        white-space: nowrap;
    }
    .src-list__delta.up {
        color: #1f9d45;
    }
    .src-list__delta.down {
        color: #d64545;
    }
    .src-type-list {
        display: grid;
        gap: 8px;
    }
</style>
